/**
 * G30's remedy, and the one assertion that makes it safe.
 *
 * THE RULE. "An anonymous reader who applies LINKS their existing account, so the uid never changes
 * and nothing they already did is orphaned"
 * (docs/design/research/26-authors-identity-application-research.md, carried as RC-26.1).
 * The corpus is equally clear about what must NOT happen: no new uid is ever created, and
 * `platform_user.auth_provider` is a last-seen record for analytics and is never the gate
 * (docs/design/04-schema.md).
 *
 * PROVED, NOT ASSUMED. Linking returning the same uid was proved against the Firebase Auth emulator
 * on 2026-08-27: the link call returned the same `localId`.
 *
 * WHY THE CHECK BELOW EXISTS ANYWAY. A link that silently produced a new uid would orphan
 * everything the reader had already done — their articles-in-progress, their profile, their
 * application — and it would do it quietly, one account at a time. The cost of checking is one
 * string comparison; the cost of not checking is a support case nobody can reconstruct. So the
 * result is refused rather than accepted, and the refusal names what happened.
 */
import type { AuthClient, AuthSession, PopupProvider } from '../firebase/auth-port';

export class LinkChangedAccountError extends Error {
  public constructor(
    public readonly previousUid: string,
    public readonly newUid: string,
  ) {
    super(
      'Connecting that account created a different MoonX account instead of adding a sign-in ' +
        'method to the existing one. Nothing has been changed; please tell support before trying ' +
        'again.',
    );
    this.name = 'LinkChangedAccountError';
  }
}

/**
 * Links a real provider onto the CURRENT account and returns the same account.
 *
 * Throws `LinkChangedAccountError` if the uid moved, `PopupBlockedError` if the browser refused the
 * window (the same first failure state as a cold-start sign-in), and the port's other errors
 * unchanged.
 */
export async function linkProviderKeepingAccount(
  client: AuthClient,
  provider: PopupProvider,
): Promise<AuthSession> {
  const before = client.currentSession();
  if (before === null) {
    throw new Error('There is no signed-in account to add a sign-in method to.');
  }
  const previousUid = before.identity.uid;

  const after = await client.linkWithPopup(provider);

  if (after.identity.uid !== previousUid) {
    throw new LinkChangedAccountError(previousUid, after.identity.uid);
  }

  return after;
}
